
import { Component, Prop, Vue } from 'vue-property-decorator';
import { SupplierModel, GlobalDefaults } from '@/models/supplier';
import { ReorderSettingParam } from '@/models/reorder_setting_param';
import { EventBus } from '@/main';
import SupplierEditDialog from '@/components/SupplierEditDialog.vue';

interface ExtendedSupplierModel extends SupplierModel {
  parametersStatus?: string;
}

@Component({
  components: {
    SupplierEditDialog
  }
})
export default class SupplierOverviewTable extends Vue {
  @Prop({ default: () => [] })
  public suppliers!: ExtendedSupplierModel[];

  @Prop({ default: false })
  public loading!: boolean;

  @Prop({ default: (): GlobalDefaults => ({}) })
  public globalDefaults!: GlobalDefaults;

  private search: string = '';

  // Dialog state
  private editDialogVisible: boolean = false;
  private selectedSupplier: SupplierModel | null = null;
  private dialogSaving: boolean = false;

  private tableHeaders = [
    {
      text: 'Supplier',
      align: 'start',
      sortable: true,
      value: 'name',
      width: '200px'
    },
    {
      text: 'Lead Time (days)',
      value: 'leadTime',
      sortable: true,
      align: 'center',
      width: '110px'
    },
    {
      text: 'Order Cycle (days)',
      value: 'reorderCycle',
      sortable: true,
      align: 'center',
      width: '130px'
    },
    {
      text: 'Safety Stock (days)',
      value: 'minStockCoverDays',
      sortable: true,
      align: 'center',
      width: '140px'
    },
    {
      text: 'Min Order Qty',
      value: 'minOrderQuantity',
      sortable: true,
      align: 'center',
      width: '110px'
    },
    {
      text: 'Max Order Qty',
      value: 'maxOrderQuantity',
      sortable: true,
      align: 'center',
      width: '110px'
    },
    {
      text: 'Scope',
      value: 'scope',
      sortable: true,
      align: 'center',
      width: '100px'
    },
    {
      text: 'Products',
      value: 'productCount',
      sortable: true,
      align: 'center',
      width: '90px'
    },
    {
      text: 'Actions',
      value: 'actions',
      sortable: false,
      align: 'center',
      width: '100px'
    },
  ];

  public get filteredSuppliers(): ExtendedSupplierModel[] {
    if (!this.search) {
      return this.suppliers;
    }

    return this.suppliers.filter(supplier =>
      supplier.name.toLowerCase().includes(this.search.toLowerCase()) ||
      (supplier.contactEmail && supplier.contactEmail.toLowerCase().includes(this.search.toLowerCase()))
    );
  }

  public navigateToSupplier(supplier: ExtendedSupplierModel) {
    this.$router.push(`/suppliers/${supplier.id}`);
  }

  public getScopeColor(scope: string): string {
    switch (scope) {
      case 'product':
        return 'success';
      case 'variant':
        return 'primary';
      default:
        return 'grey';
    }
  }

  public getScopeText(scope: string): string {
    switch (scope) {
      case 'product':
        return 'Product';
      case 'variant':
        return 'Variant';
      default:
        return scope || 'N/A';
    }
  }

  public getScopeIcon(scope: string): string {
    switch (scope) {
      case 'product':
        return 'mdi-package-variant';
      case 'variant':
        return 'mdi-shape';
      default:
        return 'mdi-help-circle';
    }
  }

  // Dialog Methods
  public openEditDialog(supplier: ExtendedSupplierModel) {
    this.selectedSupplier = new SupplierModel(supplier);
    this.editDialogVisible = true;
  }

  public handleDialogCancel() {
    this.editDialogVisible = false;
    this.selectedSupplier = null;
  }

  public handleDialogSave(data: { params: ReorderSettingParam; forceUpdate: boolean }) {
    // Emit to parent (ReorderParamSetting) for confirmation flow
    this.$emit('supplier-force-update', {
      supplier: this.selectedSupplier,
      params: data.params,
      forceUpdate: data.forceUpdate
    });

    // Close dialog and clear selected supplier to match cancel flow
    this.editDialogVisible = false;
    this.selectedSupplier = null;
  }
}
